package com.presentocracy.shared.contract

import com.presentocracy.shared.model.Birthday
import com.presentocracy.shared.model.AccessOverride
import com.presentocracy.shared.model.BaseVisibility
import com.presentocracy.shared.model.FulfilledGiftBehavior
import com.presentocracy.shared.model.Gift
import com.presentocracy.shared.model.GiftPriority
import com.presentocracy.shared.model.GiftStatus
import com.presentocracy.shared.model.ReservationVisibility
import com.presentocracy.shared.model.ReservationInfo
import com.presentocracy.shared.model.ReservationStatus
import com.presentocracy.shared.model.UserProfile
import com.presentocracy.shared.model.Wishlist
import com.presentocracy.shared.model.WishlistSortMode
import com.presentocracy.shared.model.Dossier
import com.presentocracy.shared.model.Friendship
import com.presentocracy.shared.model.UserCard
import com.presentocracy.shared.model.ReminderSettings
import com.presentocracy.shared.model.BirthdaySummary
import com.presentocracy.shared.model.NotificationItem
import kotlinx.serialization.Serializable

@Serializable
data class RegisterRequest(
    val email: String,
    val password: String,
    val username: String,
    val displayName: String,
)

@Serializable
data class RegisterResponse(
    val user: UserProfile,
    val verificationToken: String? = null,
    val verification: EmailVerificationWindow? = null,
)

@Serializable
data class EmailVerificationWindow(val serverTimeEpochMillis: Long, val resendAvailableAtEpochMillis: Long)

@Serializable
data class VerifyEmailRequest(val token: String)

@Serializable
data class VerifyEmailCodeRequest(val email: String, val code: String)

@Serializable
data class ResendVerificationRequest(val email: String)

@Serializable
data class LoginRequest(val email: String, val password: String)

@Serializable
data class RefreshRequest(val refreshToken: String)

@Serializable
data class LogoutRequest(val refreshToken: String)

@Serializable
data class PasswordResetRequest(val email: String)

@Serializable
data class PasswordResetConfirmRequest(
    val token: String,
    val newPassword: String,
)

@Serializable
data class PasswordChangeRequest(
    val currentPassword: String,
    val newPassword: String,
)

@Serializable
data class GoogleAuthRequest(val idToken: String)

@Serializable
data class UpdateProfileRequest(
    val username: String? = null,
    val displayName: String? = null,
    val avatarUrl: String? = null,
    val bio: String? = null,
    val birthday: Birthday? = null,
    val heightCm: Int? = null,
    val showBirthYear: Boolean? = null,
    val preferredCurrency: String? = null,
)

@Serializable
data class SessionResponse(
    val user: UserProfile,
    val accessToken: String,
    val refreshToken: String,
    val expiresInSeconds: Long,
)

@Serializable
data class MessageResponse(val message: String, val verification: EmailVerificationWindow? = null)

@Serializable
data class CreateWishlistRequest(
    val slug: String? = null,
    val name: String,
    val description: String? = null,
    val baseVisibility: BaseVisibility = BaseVisibility.ONLY_ME,
    val reservationVisibility: ReservationVisibility = ReservationVisibility.HIDDEN,
    val fulfilledGiftBehavior: FulfilledGiftBehavior = FulfilledGiftBehavior.MOVE_TO_BOTTOM,
    val sortMode: WishlistSortMode = WishlistSortMode.MANUAL,
)

@Serializable
data class UpdateWishlistRequest(
    val slug: String? = null,
    val name: String? = null,
    val description: String? = null,
    val baseVisibility: BaseVisibility? = null,
    val reservationVisibility: ReservationVisibility? = null,
    val fulfilledGiftBehavior: FulfilledGiftBehavior? = null,
    val sortMode: WishlistSortMode? = null,
)

@Serializable
data class CreateGiftRequest(
    val title: String,
    val description: String? = null,
    val note: String? = null,
    val productUrl: String? = null,
    val priceMinorUnits: Long? = null,
    val priceCurrency: String? = null,
    val priority: GiftPriority = GiftPriority.NORMAL,
    val quantity: Int = 1,
    val attributesJson: String? = null,
)

@Serializable
data class UpdateGiftRequest(
    val title: String? = null,
    val description: String? = null,
    val note: String? = null,
    val productUrl: String? = null,
    val priceMinorUnits: Long? = null,
    val priceCurrency: String? = null,
    val priority: GiftPriority? = null,
    val quantity: Int? = null,
    val status: GiftStatus? = null,
    val attributesJson: String? = null,
)

@Serializable
data class ReorderGiftsRequest(val giftIds: List<String>)

@Serializable
data class WishlistResponse(val wishlist: Wishlist)

@Serializable
data class GiftResponse(val gift: Gift)

@Serializable
data class WishlistAclRequest(val userId: String, val override: AccessOverride)

@Serializable
data class ShareLinkResponse(
    val token: String,
    val url: String,
    val expiresAt: Long? = null,
)

@Serializable
data class CreateShareLinkRequest(val expiresAt: Long? = null)

@Serializable
data class ReservationResponse(val giftId: String, val reservation: ReservationInfo?)

@Serializable
data class ReservationStatusRequest(val status: ReservationStatus)

@Serializable
data class Contribution(
    val id: String,
    val giftId: String,
    val contributorUserId: String? = null,
    val contributorDisplayName: String? = null,
    val amountMinorUnits: Long,
    val currency: String,
)

@Serializable
data class GroupGiftResponse(
    val giftId: String,
    val enabled: Boolean,
    val targetMinorUnits: Long? = null,
    val currency: String? = null,
    val contributions: List<Contribution> = emptyList(),
    val totalPledgedMinorUnits: Long = 0,
    val fullyPledged: Boolean = false,
)

@Serializable
data class EnableGroupGiftRequest(val targetMinorUnits: Long? = null, val currency: String? = null)

@Serializable
data class AddContributionRequest(val amountMinorUnits: Long, val currency: String)

@Serializable
data class UpdateContributionRequest(val amountMinorUnits: Long, val currency: String)

@Serializable
data class ParseProductUrlRequest(val url: String)

@Serializable
data class ProductMetadataResponse(
    val title: String? = null,
    val description: String? = null,
    val imageUrl: String? = null,
    val priceMinorUnits: Long? = null,
    val priceCurrency: String? = null,
    val sourceUrl: String,
)

@Serializable
data class ImageUploadResponse(
    val id: String,
    val url: String,
    val contentType: String,
    val width: Int,
    val height: Int,
)

@Serializable
data class FriendRequest(val userId: String)

@Serializable
data class FriendResponse(val friendship: Friendship)

@Serializable
data class DiscoveryResponse(
    val users: List<UserCard>,
    val contactMatches: Map<String, UserCard> = emptyMap(),
)

@Serializable
data class PublicProfileResponse(
    val user: UserCard,
    val wishlists: List<Wishlist>,
)

@Serializable
data class FriendProfileResponse(
    val user: UserCard,
    val bio: String? = null,
    val birthday: Birthday? = null,
    val heightCm: Int? = null,
    val wishlists: List<Wishlist> = emptyList(),
)

@Serializable
data class ContactDiscoveryRequest(val contactHashes: List<String>)

@Serializable
data class PrivacySettings(
    val searchable: Boolean,
    val contactDiscoveryEnabled: Boolean,
    val birthdayReminders: ReminderSettings,
    // Nullable additions preserve settings when an older client submits only legacy fields.
    val profileVisibility: com.presentocracy.shared.model.ProfileVisibility? = null,
    val showBio: Boolean? = null,
    val showBirthYear: Boolean? = null,
    val allowFriendRequests: Boolean? = null,
)

@Serializable
data class UpdateDiscoverySettingsRequest(
    val searchable: Boolean? = null,
    val contactDiscoveryEnabled: Boolean? = null,
)

@Serializable
data class DossierRequest(
    val subjectUserId: String,
    val dataJson: String,
    val baseVisibility: BaseVisibility = BaseVisibility.ONLY_ME,
)

@Serializable
data class DossierResponse(val dossier: Dossier)

@Serializable
data class UpcomingBirthdaysResponse(val birthdays: List<BirthdaySummary>)

@Serializable
data class ReminderSettingsRequest(val enabled: Boolean, val offsetsDays: List<Int>)

@Serializable
data class ReminderSettingsResponse(val settings: ReminderSettings)

@Serializable
data class NotificationDeviceRequest(val platform: String, val token: String)

@Serializable
data class NotificationResponse(val notifications: List<NotificationItem>)

@Serializable
data class ErrorEnvelope(
    val code: String,
    val message: String,
    val details: Map<String, String> = emptyMap(),
)
